/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/* eslint-disable */
import React, { useState, useEffect } from "react";
import { message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    TableComponent
} from "../../../CommonComponents";
import "../../styles.scss";
import Heading from "../../../../../components/Heading";
import { ReportsService } from "../../../../../services/api/reports";
import { downloadShipmentInscanClickableReport } from "../../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../../hooks/useRequestCancellation";
import { useNavigate, useLocation } from "react-router-dom";
import { showErrorMessage } from "../../../../../utils";

const ShipmentInscanClickableReport = () => {
    // Global request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);

    const [hubUser, setHubUser] = useState();

    const navigate = useNavigate();
    const location = useLocation();

    // Set user role
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo"));
        setHubUser(user?.roles?.[0]);
    }, []);

    // Fetch report data
    useEffect(() => {
        if (!location.state) return;
        const { operationDate, premiseId } = location.state;
        console.log("ShipmentInscanClickableReport - location.state:", location.state);
        console.log("ShipmentInscanClickableReport - operationDate:", operationDate, "premiseId:", premiseId);
        if (!operationDate || !premiseId) return;
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, location.state, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        // 🔥 Create new request for cancellation support
        const { signal, requestId } = createNewRequest("shipment-inscan-clickable-report");

        setLoading(true);
        let shouldUpdateState = true;

        try {
            // Get form parameters from location.state
            const formParams = location.state || {};
            const { operationDate, premiseId } = formParams;
            if (!operationDate || !premiseId) {
                setLoading(false);
                return;
            }

            setReportData([]);

            // Format the date to YYYY-MM-DD format as required by API
            let formattedDate;
            if (moment(operationDate).isValid()) {
                formattedDate = moment(operationDate).format("YYYY-MM-DD");
            } else {
                // Try to parse different date formats and convert to YYYY-MM-DD
                const parsedDate = moment(operationDate, ["DD-MM-YYYY", "MM-DD-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"]);
                if (parsedDate.isValid()) {
                    formattedDate = parsedDate.format("YYYY-MM-DD");
                } else {
                    // If all parsing fails, use the original value
                    formattedDate = operationDate;
                }
            }

            const params = {
                operationDate: formattedDate,
                premiseId: premiseId,
                page: (pagination.current - 1),
                size: pagination.pageSize
            };

            const { data, count } = await ReportsService.getShipmentInscanClickableReport(params, { signal });

            console.log("ShipmentInscanClickableReport - API response:", { data, count });
            console.log("ShipmentInscanClickableReport - API params:", params);

            // 🔥 Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                shouldUpdateState = false;
            } else {
                setReportData(data || []);
                setPagination(prev => ({
                    ...prev,
                    total: count || 0,
                }));
            }
        } catch (error) {
            if (error.name === "AbortError") {
                console.log("🔥 Request was cancelled at HTTP level - no state update");
                return;
            }

            if (shouldUpdateState && isLatestRequest(requestId)) {
                showErrorMessage(error?.message || "Failed to fetch shipment inscan clickable report");
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0,
                }));
            }
        } finally {
            if (shouldUpdateState && isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    const handleTableChange = (paginationInfo) => {
        setPagination(prev => ({
            ...prev,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize
        }));
    };

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const formParams = location.state || {};
            const { operationDate, premiseId } = formParams;

            // Format the date to YYYY-MM-DD format for download
            let formattedDate;
            if (moment(operationDate).isValid()) {
                formattedDate = moment(operationDate).format("YYYY-MM-DD");
            } else {
                // Try to parse different date formats and convert to YYYY-MM-DD
                const parsedDate = moment(operationDate, ["DD-MM-YYYY", "MM-DD-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"]);
                if (parsedDate.isValid()) {
                    formattedDate = parsedDate.format("YYYY-MM-DD");
                } else {
                    // If all parsing fails, use the original value
                    formattedDate = operationDate;
                }
            }

            const downloadParams = {
                operationDate: formattedDate,
                premiseId: premiseId
            };

            await downloadShipmentInscanClickableReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleBack = () => {
        navigate("/shipments-inscan-vs-outscan-report");
    };

    const getTableColumns = () => {
        return [
            {
                title: "No.",
                dataIndex: "index",
                key: "index",
                width: 60,
                render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
            },
            {
                title: "Inscan Datetime",
                dataIndex: "inscanDatetime",
                key: "inscanDatetime",
                sorter: true,
                width: 200,
            },
            {
                title: "Outscan Datetime",
                dataIndex: "outscanTime",
                key: "outscanTime",
                sorter: true, // Assuming this column should also be sortable
                width: 200,
                align: "center",
            },
            {
                title: "AWB Number",
                dataIndex: "awbNumber",
                key: "awbNumber",
                sorter: true,
                width: 150,
                render: (text, record) => (
                    <span
                        style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => {
                            if (text) {
                                const url = `/shipment-tracker?awb=${text}`;
                                window.open(url, "_blank");
                            }
                        }}
                    >
                        {text || "-"}
                    </span>
                )
            },
            {
                title: "Origin Hub Name",
                dataIndex: "originHubName",
                key: "originHubName",
                sorter: true,
                width: 200,
            },
            {
                title: "Destination Hub Name",
                dataIndex: "destinationHubName",
                key: "destinationHubName",
                sorter: true,
                width: 200,
            },
            {
                title: "Current Hub Name",
                dataIndex: "currentHubName",
                key: "currentHubName",
                sorter: true,
                width: 200,
            }
        ];
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading
                    title="Shipment Inscan Clickable Report"
                    navigateTo="/shipments-inscan-vs-outscan-report"
                    showBackButton={true}
                    onBackClick={handleBack}
                >
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloadLoading}
                        onClick={handleDownload}
                    >
                        Download Report
                    </Button>
                </Heading>
            </div>
            <TableComponent
                columns={getTableColumns()}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey={(record, index) => record.awbNumber || record.id || record.awb || index}
                style={{ marginTop: 32 }}
            />
        </div>
    );
};

export default ShipmentInscanClickableReport;
